import type { ReactNode } from 'react';
import { useFeatureAvailability } from '../contexts/FeatureAvailabilityContext.js';
import {
  featureIsAvailable,
  type FeatureKey,
} from '../lib/route-permissions.js';

/**
 * What a direct URL to a not-yet-available surface says. Per feature,
 * because "not set up" means something different each time: a mailbox
 * nobody has provisioned versus a server build that has not been deployed.
 * Exhaustive over FeatureKey — a new gated feature without copy is a
 * compile error, never a blank page.
 */
const UNAVAILABLE_COPY: Record<FeatureKey, { title: string; body: string }> = {
  vaxInbox: {
    title: 'Not set up yet',
    body:
      "This facility isn't receiving vaccination records by email yet. Once the mailbox is set up, records emailed in will appear here for you to check and approve.",
  },
  ownerSettings: {
    title: 'Not available yet',
    body:
      "This facility's server hasn't been updated with Settings yet. It appears as soon as the update lands — usually the same day. Nothing you've set elsewhere is affected.",
  },
  // Sawyer 16 staff-messaging (E4)
  staffMessages: {
    title: 'Not available yet',
    body:
      "This facility's server hasn't been updated with Team messages yet. It appears as soon as the update lands. Nothing else is affected.",
  },
};

type FeatureRouteProps = {
  /** The provisioning gate this surface depends on. */
  feature: FeatureKey;
  children: ReactNode;
};

/**
 * Per-route PROVISIONING gate (#551) — the sibling of `RouteGuard`.
 *
 * RouteGuard answers "may this role use this surface". This answers a
 * different question that has nothing to do with the caller: "does this
 * surface exist in this facility yet". Some features depend on ops work
 * (a mailbox, a DNS record, a provider account) that lands on its own
 * schedule, and frontends auto-deploy on merge while the server does not —
 * so a merged page is live for every existing tenant seconds later,
 * whether or not the thing it talks to exists.
 *
 * Hiding only the nav item is not enough: the route stays reachable by
 * URL, by browser history, and by a bookmark a staffer made during a demo.
 * The gate has to be on the route.
 *
 * Behavior when unavailable: an honest in-place explanation, same shape as
 * RouteGuard's 403 (chrome intact, no redirect, no white screen). It does
 * NOT pretend the feature is broken or empty — a permanently-empty inbox
 * telling a receptionist to route vets to a nonexistent address is exactly
 * the failure this gate exists to prevent (DESIGN-PHILOSOPHY P5, never lie
 * about data).
 *
 * While the availability probe is in flight, render nothing rather than a
 * "not available" claim we might immediately retract — a one-frame flash
 * of the wrong answer is its own small lie.
 */
export function FeatureRoute({
  feature,
  children,
}: FeatureRouteProps): JSX.Element {
  const { features, loading } = useFeatureAvailability();

  if (loading) return <section className="ferro-admin-page" aria-busy="true" />;

  if (featureIsAvailable(feature, features)) {
    return <>{children}</>;
  }

  const copy = UNAVAILABLE_COPY[feature];
  return (
    <section
      className="ferro-admin-page"
      role="status"
      aria-live="polite"
      data-testid="route-feature-unavailable"
    >
      <h1 className="ferro-section__title">{copy.title}</h1>
      <p className="ferro-muted">{copy.body}</p>
    </section>
  );
}
